<template>
  <div class="lti-view">
    <div v-if="userIsRegistered" class="lti-main">
      <div class="lti-heading">{{ t('heading') }}</div>
      <div class="lti-table-wrap">
        <table class="lti-table">
          <thead>
            <tr>
              <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(wall, index) in ltiWalls" :key="index">
              <td :data-label="headers[0]">
                <span class="lti-wall-link" @click="openTalkwall(wall._id)">{{ wall.details.title }}</span>
              </td>
              <td :data-label="headers[1]">{{ wall.users.owner.username }}</td>
              <td :data-label="headers[2]">{{ formatLtiDate(wall.status.created) }}</td>
              <td :data-label="headers[3]">{{ formatLtiDate(wall.status.lastOpened) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- < div class="lti-new-wall" >
  <div class="lti-new-wall-button" @click="newTalkwall()" >
    <div class="lti-new-wall-icon">+</div>
        </div >
  <span class="lti-new-wall-label">New wall</span>
      </div > -->
    </div >
  <LoadingScreen v-else />
  </div >
</template >
<script setup lang="ts">
  import { computed, onBeforeMount, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { clientBaseUrl, GroupType } from '@/constants'

  import { useUserStore } from '@/composables/userStore'
  import useWallStore from '@/composables/wallStore'

  type LtiLocale = 'en' | 'no'

  const messages = {
    en: {
      heading: 'Walls for this subject',
      headerTitle: 'Title',
      headerOwner: 'Owner',
      headerCreated: 'Created',
      headerLastUpdated: 'Last updated',
    },
    no: {
      heading: 'Tavler i dette emnet',
      headerTitle: 'Tittel',
      headerOwner: 'Eier',
      headerCreated: 'Laget',
      headerLastUpdated: 'Sist oppdatert',
    },
  }

  const CANVAS_LOCALE_TIMEOUT_MS = 2500

  const userStore = useUserStore()
  const wallStore = useWallStore()
  const router = useRouter()
  const route = useRoute()

  const { t, locale } = useI18n({ messages, locale: 'en' })

  const headers = computed(() => [t('headerTitle'), t('headerOwner'), t('headerCreated'), t('headerLastUpdated')])

  /** Canvas I18n medium: en `%b %-d, %Y` → Oct 19, 2026; nb `%-d. %b %Y` → 19. okt. 2026 */
  function formatLtiDate(date: Date): string {
    const intlLocale = locale.value === 'no' ? 'nb-NO' : 'en-US'
    return date.toLocaleDateString(intlLocale, { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const courseId = computed(() => route.query.course)

  const ltiWalls = computed(() => {
    const walls = wallStore.getters.allWalls.value
    const user = userStore.getters.user.value
    const course = courseId.value
    if (!user || !course) return []

    const userId = user._id
    const courseStr = String(course)

    return walls.filter((wall) => {
      const isOwner = wall.users.owner.user_id === userId
      const sharedToCourse = wall.users.shares.some(
        (share) => String(share.group_id) === courseStr && share.groupType === GroupType.CANVAS,
      )
      const linkedToCourse = String(wall.details.canvasCourseID) === courseStr

      if (!isOwner) return true
      return sharedToCourse || linkedToCourse
    })
  })

  function normalizeLtiLocale(raw: string | undefined | null): LtiLocale {
    if (!raw) return 'en'
    const lower = raw.toLowerCase()
    if (lower === 'no' || lower.startsWith('nb') || lower.startsWith('nn')) return 'no'
    return 'en'
  }

  function requestCanvasLocale(): Promise<LtiLocale> {
    return new Promise((resolve) => {
      if (window.parent === window) {
        resolve('en')
        return
      }

      let settled = false
      const finish = (value: LtiLocale) => {
        if (settled) return
        settled = true
        window.removeEventListener('message', onMessage)
        clearTimeout(timer)
        resolve(value)
      }

      const onMessage = (event: MessageEvent) => {
        const data = event.data
        if (!data || typeof data !== 'object') return

        const subject = data.subject as string | undefined
        if (subject !== 'lti.getPageSettings.response' && subject !== 'lti.getPageSettings') return
        if (!data.pageSettings) return

        finish(normalizeLtiLocale(data.pageSettings.locale as string | undefined))
      }

      const timer = window.setTimeout(() => finish('en'), CANVAS_LOCALE_TIMEOUT_MS)

      window.addEventListener('message', onMessage)
      window.parent.postMessage({ subject: 'lti.getPageSettings' }, '*')
    })
  }

  async function applyLtiLocale() {
    const queryLocale = route.query.locale
    if (typeof queryLocale === 'string' && queryLocale.length > 0) {
      locale.value = normalizeLtiLocale(queryLocale)
      return
    }
    locale.value = await requestCanvasLocale()
  }

  onMounted(() => {
    applyLtiLocale()
  })

  onBeforeMount(async () => {
    const handoff = route.query.handoff
    if (typeof handoff === 'string' && handoff.length > 0) {
      sessionStorage.setItem('ltiHandoff', handoff)
      const query = { ...route.query }
      delete query.handoff
      await router.replace({ path: '/lti', query })
    }
    if (!userStore.getters.user.value) {
      const res = await userStore.actions.getUserDetails()
      if (!res.success) return router.push('/login')
    }
    if (!userIsRegistered.value && !router.options.history.state.back?.toString().includes('login')) router.back() // do not allow anonymous users to get to the dashboard
    if (userIsRegistered.value) {
      const res = await wallStore.actions.getUserWalls('lti')
      if (!res.success || res.error) router.push('/login')
    }
  })

  const userIsRegistered = computed(() => {
    const user = userStore.getters.user.value
    return !user || !user.status ? false : user.status.isRegistered
  })

  function withLtiHandoff(url: string): string {
    const handoff = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ltiHandoff') : null
    if (!handoff) return url
    const sep = url.includes('?') ? '&' : '?'
    return `${url}${sep}handoff=${encodeURIComponent(handoff)}`
  }

  async function openTalkwall(wall_id: string) {
    const clientPort = import.meta.env.VITE_CLIENT_PORT || '8080'
    const url =
      import.meta.env.VITE_NODE_ENV === 'testing' || import.meta.env.VITE_NODE_ENV === 'production' ? clientBaseUrl : `${clientBaseUrl}:${clientPort}`
    window.open(withLtiHandoff(`${url}/wall?id=${wall_id}`))
  }

  async function newTalkwall() {
    const clientPort = import.meta.env.VITE_CLIENT_PORT || '8080'
    const url =
      import.meta.env.VITE_NODE_ENV === 'testing' || import.meta.env.VITE_NODE_ENV === 'production' ? clientBaseUrl : `${clientBaseUrl}:${clientPort}`
    console.log(`${url}/dashboard/newWall/${courseId.value}`)
    window.open(withLtiHandoff(`${url}/dashboard/newWall/${courseId.value}`))
  }
</script>
<style src="./ltistyles.css" scoped></style>
